<template>
  <div class="dictation-root">
    <div class="dictation-wrapper d-inline-flex align-center">
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <span v-bind="attrs" v-on="on">
          <!--
            salvia-864: PRESSIONAR E SEGURAR, nao mais alternar.

            Antes era um toggle: um clique comecava, outro parava. O modo novo,
            pedido na reuniao de 14/09, e o de radio: segurou, grava; soltou, para.

            Por que isso importa alem da estetica: com toggle, esquecer de clicar
            de novo deixa o microfone ligado — na consulta isso e a paciente sendo
            gravada sem que ninguem perceba. Segurando, soltar encerra sempre.

            O SOLTAR E OUVIDO NA JANELA, nao no botao — e isso nao e detalhe. A
            primeira versao usava `@mouseup`/`@mouseleave` no proprio botao e
            parava sozinha no instante do clique: o rotulo muda de "Transcrever"
            (11 caracteres) para "Gravando" (8), o botao ENCOLHE debaixo do
            cursor, o ponteiro fica de fora e o `mouseleave` disparava. Quanto
            mais a direita o clique, mais garantido o bug.

            Ouvindo na janela, soltar em QUALQUER lugar encerra — inclusive fora
            do botao, que era a protecao que o `mouseleave` tentava dar. E o
            `min-width` no CSS impede o pulo de largura, que alem de quebrar
            tambem ficava feio.

            Teclado: quem navega por teclado nao consegue "segurar" um clique, mas
            consegue manter Espaco/Enter pressionado — por isso keydown/keyup, com
            guarda contra o autorepeat do navegador. Sem isso a funcao sumiria para
            essas pessoas.
          -->
          <v-btn
            size="small"
            :variant="isRecording ? 'flat' : 'outlined'"
            :color="isRecording ? 'error' : 'success'"
            :disabled="!speechApiAvailable"
            class="dictation-btn"
            @mousedown.prevent="pressionar"
            @touchstart.prevent="pressionar"
            @keydown.space.enter.prevent="pressionarPorTecla"
            @keyup.space.enter.prevent="soltar"
          >
            <v-icon size="18" class="mr-1">
              {{ isRecording ? "mdi-record" : "mdi-microphone" }}
            </v-icon>
            {{ isRecording ? "Gravando" : "Transcrever" }}
          </v-btn>
        </span>
      </template>
      <span>{{ tooltipText }}</span>
    </v-tooltip>

    <!-- Indicador "ouvindo" -->
    <transition name="fade">
      <div v-if="isRecording" class="listening-indicator ml-2">
        <div class="recording-dot mx-1"></div>
        <div class="recording-dot mx-1"></div>
        <div class="recording-dot mx-1"></div>
        <span class="text-caption ml-1">Ouvindo…</span>
      </div>
    </transition>
    </div>

    <!-- Preview ao vivo do que está sendo reconhecido -->
    <transition name="fade">
      <div v-if="isRecording" class="live-preview mt-2">
        <!-- salvia-821: o texto e a NUMERACAO abaixo eram da prescricao, onde cada
             pausa vira um item da receita. No chat do assistente nao existe
             "proximo item" — e uma pergunta continua —, e a lista numerada poluia a
             tela. Viraram props com os valores da prescricao como padrao, entao
             aquela tela nao muda em nada. -->
        <div class="live-preview-header">
          <v-icon size="14" class="mr-1">mdi-text-recognition</v-icon>
          <span v-html="hint"></span>
        </div>
        <ol v-if="numbered && (liveLines.length || liveInterim)" class="live-lines">
          <li v-for="(line, i) in liveLines" :key="i">{{ line }}</li>
          <li v-if="liveInterim" class="live-interim">{{ liveInterim }}</li>
        </ol>
        <p v-else-if="liveLines.length || liveInterim" class="live-paragraph">
          {{ liveLines.join(" ") }}
          <span v-if="liveInterim" class="live-interim">{{ liveInterim }}</span>
        </p>
        <div v-else class="live-empty">{{ emptyText }}</div>
      </div>
    </transition>
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";

/**
 * Botão de ditado por voz (Web Speech API, pt-BR).
 *
 * Eventos:
 *   - `transcript`  (string)  — texto final reconhecido em cada "frase" (acumular no pai).
 *   - `interim`     (string)  — texto provisório enquanto o usuário fala.
 *   - `recording`   (boolean) — true ao iniciar, false ao parar.
 *
 * Uso típico no editor: ao receber `transcript`, inserir no cursor com
 * document.execCommand('insertText', false, text + ' ').
 */
export default {
  name: "DictationButton",

  props: {
    /**
     * salvia-864: NAO controla mais a cor do botao — ele e verde ("Transcrever")
     * e vira vermelho enquanto grava, por decisao de interface de 14/09, igual em
     * todas as telas. A prop segue valendo para o resto do componente (preview),
     * e continua aceita para nao quebrar quem ja a passa, como o PrescriptionDialog.
     */
    color: { type: String, default: "primary" },
    // Idioma do reconhecimento (default pt-BR).
    lang: { type: String, default: "pt-BR" },
    // salvia-821: os tres abaixo nasceram com o texto da PRESCRICAO como padrao,
    // de proposito — quem ja usava o componente nao muda. Quem dita algo que nao e
    // uma lista de itens (o chat do assistente) passa os seus.
    hint: {
      type: String,
      default:
        'Reconhecendo… pause ou diga <strong>"próximo"</strong> para o próximo item',
    },
    emptyText: { type: String, default: "Fale o nome do item…" },
    numbered: { type: Boolean, default: true },
  },

  emits: ["transcript", "interim", "recording"],

  setup() {
    const { success, error, warning, info } = useSnackbar();
    return { success, error, warning, info };
  },

  data() {
    return {
      recognition: null,
      speechApiAvailable: false,
      isRecording: false,
      recognitionActive: false,
      networkError: false,
      restartTimer: null,
      // Preview ao vivo do que está sendo reconhecido (apenas exibição).
      liveLines: [],
      liveInterim: "",
    };
  },

  computed: {
    tooltipText() {
      if (!this.speechApiAvailable) {
        return "Reconhecimento de voz não disponível neste navegador";
      }
      return this.isRecording
        ? "Solte para parar"
        : "Segure para transcrever";
    },
  },

  mounted() {
    this.initSpeechRecognition();
  },

  beforeDestroy() {
    this.cleanup();
  },

  watch: {
    isRecording(val) {
      this.$emit("recording", val);
    },
  },

  methods: {
    initSpeechRecognition() {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        this.speechApiAvailable = false;
        return;
      }

      this.speechApiAvailable = true;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = this.lang;
      this.recognition.maxAlternatives = 3;

      this.recognition.onstart = () => {
        this.recognitionActive = true;
      };

      this.recognition.onresult = (event) => {
        let interim = "";
        let finalText = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          let best = result[0].transcript;
          let bestConf = result[0].confidence || 0.5;
          for (let j = 1; j < result.length && j < 3; j++) {
            if (result[j].confidence > bestConf) {
              best = result[j].transcript;
              bestConf = result[j].confidence;
            }
          }
          if (result.isFinal) finalText += best;
          else interim += best;
        }

        if (finalText) {
          // Cada "final" da API corresponde a uma pausa → vira uma linha/item.
          this.liveLines.push(finalText.trim());
          this.$emit("transcript", finalText);
        }
        this.liveInterim = interim;
        this.$emit("interim", interim);
      };

      this.recognition.onerror = (event) => {
        this.recognitionActive = false;
        switch (event.error) {
          case "not-allowed":
            this.error("Permissão de microfone negada.");
            this.stop();
            return;
          case "network":
            this.networkError = true;
            this.warning("Erro de rede no reconhecimento de voz.");
            return;
          case "no-speech":
          case "aborted":
            return;
          case "audio-capture":
            this.error("Erro ao capturar áudio.");
            return;
        }
        // tenta reiniciar
        if (this.isRecording && !this.networkError) {
          this.scheduleRestart();
        }
      };

      this.recognition.onend = () => {
        this.recognitionActive = false;
        // Web Speech API "termina" sozinha de tempos em tempos; reinicia se ainda
        // estamos no modo gravação.
        if (this.isRecording && !this.networkError) {
          this.scheduleRestart();
        }
      };
    },

    scheduleRestart() {
      clearTimeout(this.restartTimer);
      this.restartTimer = setTimeout(() => {
        this.safeStart();
      }, 150);
    },

    safeStart() {
      if (!this.recognition || this.recognitionActive) return;
      try {
        this.recognition.start();
      } catch (e) {
        if (String(e?.message || "").includes("already started")) {
          this.recognitionActive = true;
        }
      }
    },

    // salvia-864: substitui o `toggle()`. Ver o comentario no template.
    pressionar() {
      if (this.isRecording) return; // ja gravando (autorepeat, toque duplo)
      this.ouvirSoltarNaJanela();
      this.start();
    },

    /**
     * Registra o "soltar" na JANELA enquanto estiver gravando.
     *
     * No botao nao serve: ele muda de tamanho ao entrar em gravacao e escapa de
     * baixo do cursor. Na janela, soltar em qualquer lugar encerra — que e o
     * comportamento que se espera de um botao de radio.
     */
    ouvirSoltarNaJanela() {
      if (this._soltarNaJanela) return;
      this._soltarNaJanela = () => this.soltar();
      window.addEventListener("mouseup", this._soltarNaJanela);
      window.addEventListener("touchend", this._soltarNaJanela);
      window.addEventListener("touchcancel", this._soltarNaJanela);

      // ⚠️ O `blur` da janela FOI REMOVIDO daqui (15/09).
      //
      // Eu o adicionei como guarda para o alt-tab: se a pessoa trocar de
      // aplicativo segurando o botao, o `mouseup` acontece fora e o microfone
      // ficaria ligado. O raciocinio era bom; o efeito colateral, nao.
      //
      // Iniciar reconhecimento de voz pode tirar o foco da janela (indicador de
      // microfone, prompt de permissao). Quando isso acontece, o `blur` dispara
      // no MESMO instante em que a gravacao comeca — e o `soltar()` encerra
      // antes de a pessoa falar. Ou seja: a guarda contra o microfone ficar
      // ligado estava impedindo o microfone de ligar.
      //
      // Fica sem essa protecao de proposito. O caso que ela cobria e raro e o
      // prejuizo e um microfone aberto ate o proximo clique; o caso que ela
      // quebrava era TODO uso da funcao.
    },

    pararDeOuvirSoltar() {
      if (!this._soltarNaJanela) return;
      window.removeEventListener("mouseup", this._soltarNaJanela);
      window.removeEventListener("touchend", this._soltarNaJanela);
      window.removeEventListener("touchcancel", this._soltarNaJanela);
      this._soltarNaJanela = null;
    },

    /**
     * O navegador dispara `keydown` repetidamente enquanto a tecla fica
     * pressionada. Sem a guarda do `repeat`, cada repeticao chamaria `start()` e
     * reiniciaria o reconhecimento no meio da fala.
     */
    pressionarPorTecla(evento) {
      if (evento?.repeat) return;
      this.pressionar();
    },

    soltar() {
      this.pararDeOuvirSoltar();
      if (!this.isRecording) return;
      this.stop();
    },

    start() {
      if (!this.speechApiAvailable) return;
      this.networkError = false;
      this.liveLines = [];
      this.liveInterim = "";
      this.isRecording = true;
      this.safeStart();
      this.success("Ditando… fale agora.");
    },

    stop() {
      this.isRecording = false;
      this.liveInterim = "";
      this.$emit("interim", "");
      clearTimeout(this.restartTimer);
      try {
        this.recognition?.stop();
      } catch (e) {}
      this.recognitionActive = false;
    },

    cleanup() {
      // salvia-864: os listeners moram na JANELA, entao nao somem junto com o
      // componente. Sem isto, sair da tela gravando deixa handler orfao apontando
      // para uma instancia destruida.
      this.pararDeOuvirSoltar();
      clearTimeout(this.restartTimer);
      try {
        this.recognition?.abort();
      } catch (e) {}
      this.recognition = null;
      this.recognitionActive = false;
      this.isRecording = false;
    },
  },
};
</script>

<style scoped>
.dictation-btn {
  /* salvia-864: largura fixa — o rotulo troca de "Transcrever" para
     "Gravando" e o botao encolheria debaixo do cursor. */
  min-width: 148px;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  border-radius: 6px !important;
  height: 32px !important;
}

.dictation-root {
  display: block;
}

.listening-indicator {
  display: inline-flex;
  align-items: center;
  color: var(--ds-danger-mid);
  font-weight: 600;
}

/* ═══ Preview ao vivo ═══ */
.live-preview {
  border: 1px dashed #A9D6C5;
  background: var(--ds-blue-50);
  border-radius: 8px;
  padding: 8px 12px;
  max-width: 100%;
}

.live-preview-header {
  font-size: 11px;
  color: var(--ds-primary);
  font-weight: 600;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.live-paragraph {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
}
.live-lines {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: var(--ds-text);
}

.live-lines li {
  margin: 2px 0;
}

.live-interim {
  color: var(--ds-text-muted);
  font-style: italic;
  list-style: none;
}

.live-empty {
  font-size: 12px;
  color: var(--ds-text-muted);
  font-style: italic;
}

.recording-dot {
  width: 6px;
  height: 6px;
  background-color: var(--ds-red);
  border-radius: 50%;
  animation: pulse-dot 1.5s infinite;
}

.recording-dot:nth-child(2) {
  animation-delay: 0.2s;
}
.recording-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.3; transform: scale(0.6); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
